import { fraction, fromNet, rateFor, roundCents, toCents, toNet, toNumber, toStored } from "../../shared/money";
import { type ItemPricing, LABOUR_WORD, type MarkupTier, type PriceListData, type PricingRule, type GroupTaxBasis } from "./meta";

/*
 * The price formula, shared by the server (what gets saved to ServiceM8) and the screens (the
 * breakdown on an item), so the two always agree. No server or browser imports.
 *
 * Two ways, as ServiceM8's pricing strategies:
 *   Markup ("percent"): cost + a % of it (typed, or a markup group's, by its tiers). A new cost gives a new price (to update).
 *   Fixed price ("manual"): a price, kept: a new cost gives the same price (its markup shown moves).
 * Worked out before tax, then put in the item's basis, to the cent.
 *
 * `tax` is always the item's own rate as a fraction (0.1 for 10%; see shared/money.ts rateFor).
 *
 * An item using a fixed-price group is taxed as the group says (its rate, with or without tax): one that
 * isn't yet is priced as it will be once it is (asGroupTaxed), and updating it sets both.
 */

export interface Basis {
    /** Whether the item's price and cost include tax. */
    includesTax: boolean;
    /** The item's tax rate as a fraction. */
    tax: number;
}

export interface PriceBreakdown {
    mode: PricingRule["mode"];
    /** Before tax. */
    costNet: number;
    markupNet: number;
    baseNet: number;
    totalNet: number;
    /** The total in the item's basis, unrounded. */
    total: number;
    /** The item's price, as ServiceM8 stores it (item's basis, to the cent). */
    price: string;
}

const basisOf = (item: { price_includes_taxes: boolean }, tax: number): Basis => ({ includesTax: item.price_includes_taxes, tax });

/** An item's tax rate: its own, else the account's default (its uuid, blank without any), and that as a fraction. */
export interface ItemRate {
    uuid: string;
    tax: number;
}

type CostedItem = { uuid: string; cost: string; price_includes_taxes: boolean; tax_rate_uuid: string };

/** Whether an item is taxed as its group says (at no tax, with or without it is the same thing). */
export const taxedAsGroup = (item: { price_includes_taxes: boolean }, rate: ItemRate, group: GroupTaxBasis) =>
    rate.uuid === group.rateUuid && (item.price_includes_taxes === group.includesTax || group.tax === 0);

/** The item taxed as its group says: the same cost before tax, in the group's basis. */
export function asGroupTaxed<T extends { cost: string; price_includes_taxes: boolean }>(item: T, rate: ItemRate, group: GroupTaxBasis) {
    const cost = toCents(fromNet(toNet(toNumber(item.cost), item.price_includes_taxes, rate.tax), group.includesTax, group.tax));
    return { ...item, cost, price_includes_taxes: group.includesTax, tax_rate_uuid: group.rateUuid };
}

/**
 * What its pricing works an item's price out from: the item as it is, or (using a fixed-price group, taxed otherwise) as it
 * will be once it's taxed as the group says (`taxTo`).
 */
export function pricedAs<T extends { cost: string; price_includes_taxes: boolean }>(item: T, rate: ItemRate, pricing: ItemPricing | undefined) {
    const group = pricing?.groupTax;
    if (!group || taxedAsGroup(item, rate, group)) return { item, tax: rate.tax, taxTo: null };
    return { item: asGroupTaxed(item, rate, group), tax: group.tax, taxTo: group };
}

/** A plain price: typed in, nothing worked out (a fixed price). */
export const isPlain = (r: PricingRule) => r.mode === "manual";

/**
 * A markup with no cost to put it on: a $0 cost almost always means one isn't set yet, not a free part,
 * so it gives no price at all (rather than $0). The item keeps the price it has, flagged, until it has
 * a cost. A fixed price needs none.
 */
export const missingCost = (item: { cost: string }, rule: Pick<PricingRule, "mode"> | undefined) =>
    rule?.mode === "percent" && toNumber(item.cost) <= 0;

/** A markup group's tier for a cost (before tax): the one it's in, each from its minimum cost to the next one's. */
export function tierFor(tiers: readonly MarkupTier[], costNet: number) {
    let found = tiers[0];
    for (const t of tiers) if (costNet + 1e-9 >= toNumber(t.minCost)) found = t;
    return found;
}

/** A markup's % on a cost (before tax): its group's tier for it, else as typed. */
export function markupOf(rule: Pick<PricingRule, "tiers" | "markupValue">, costNet: number) {
    return rule.tiers?.length ? (tierFor(rule.tiers, costNet)?.markup ?? rule.markupValue) : rule.markupValue;
}

/** The price an item's pricing gives it, or null with no cost to work from (missingCost). */
export function calcPrice(item: { cost: string; price_includes_taxes: boolean }, tax: number, rule: PricingRule | undefined): PriceBreakdown | null {
    if (!rule || missingCost(item, rule)) return null;
    const basis = basisOf(item, tax);
    const costNet = rule.mode === "percent" ? toNet(toNumber(item.cost), basis.includesTax, tax) : 0;
    // What it's worked out from, before tax: a fixed price; or (a markup, on the cost) nothing more.
    const baseNet = rule.mode === "manual" ? toNumber(rule.basePrice) : 0;
    const markupNet = rule.mode === "percent" ? (costNet * toNumber(markupOf(rule, costNet))) / 100 : 0;
    const totalNet = costNet + markupNet + baseNet;
    const total = fromNet(totalNet, basis.includesTax, tax);
    return { mode: rule.mode, costNet, markupNet, baseNet, totalNet, total, price: toCents(roundCents(total)) };
}

/** A rule amount stored before tax, as shown and typed for the item (its basis). */
export const shownAmount = (stored: string, basis: Basis) => toCents(fromNet(toNumber(stored), basis.includesTax, basis.tax));
/** A typed amount (in the item's basis), as stored: before tax. */
export const storedAmount = (typed: string, basis: Basis) => toStored(toNet(toNumber(typed), basis.includesTax, basis.tax));

/**
 * What an item's pricing adds on top of its cost, exactly (before tax, unrounded): from its rule,
 * or without one, its typed price. For switching to a markup keeping the price it gives.
 */
export function exactMarkup(
    item: { cost: string; price: string; price_includes_taxes: boolean },
    tax: number,
    rule: PricingRule | undefined,
) {
    const costNet = toNet(toNumber(item.cost), item.price_includes_taxes, tax);
    const result = calcPrice(item, tax, rule);
    const totalNet = result ? result.totalNet : toNet(toNumber(item.price), item.price_includes_taxes, tax);
    return { costNet, markupNet: totalNet - costNet };
}

/** A markup as a percentage of the cost, stored exactly (shown to 2 places: shownPercent). */
export function percentFor(markupNet: number, costNet: number) {
    const pct = costNet > 0 ? (Math.max(0, markupNet) / costNet) * 100 : 0;
    // 8 places: to the cent on any cost (4 would be a cent out on a $10,000 one).
    return String(Math.round(pct * 1e8) / 1e8);
}

/** A fixed price's markup on its cost (%): its price over that cost. Null with no cost to tell it from. */
export const markupOfBase = (basePrice: string, costNet: number) => (costNet > 0 ? ((toNumber(basePrice) - costNet) / costNet) * 100 : null);

/** A fixed price for a markup on its cost (typed, %), stored before tax, to 8 places like percentFor (so the markup reads back as typed on any cost). */
export const baseForMarkup = (pct: string, costNet: number) => String(Math.round(costNet * (1 + toNumber(pct) / 100) * 1e8) / 1e8);

/** A markup's % that gives a total price (typed, in `basis`) on `onNet` (its cost, before tax), stored exactly like percentFor. Null: nothing to put it on. */
export function markupForTotal(total: string, basis: Basis, onNet: number) {
    if (onNet <= 0) return null;
    const pct = ((toNet(toNumber(total), basis.includesTax, basis.tax) - onNet) / onNet) * 100;
    return String(Math.round(pct * 1e8) / 1e8);
}

/**
 * Whether two rules read the same on screen: the fields their way of pricing uses, each as it's shown
 * (money to the cent in `basis`, a % to a millionth). Fields it doesn't use (a price left over from a
 * switch) and tiny differences from working a value out again don't count: an edit undone is no edit.
 */
export function sameRuleShown(a: PricingRule, b: PricingRule, basis: Basis) {
    if (a.mode !== b.mode) return false;
    if (JSON.stringify(a.tiers ?? null) !== JSON.stringify(b.tiers ?? null)) return false;
    if (a.tiers) return true;
    if (a.mode === "percent") return Math.abs(toNumber(a.markupValue) - toNumber(b.markupValue)) < 1e-6;
    return shownAmount(a.basePrice, basis) === shownAmount(b.basePrice, basis);
}

/** A stored percentage as it's shown: to 2 places (stored exactly, so switching back gives the same amount). */
export const shownPercent = (stored: string) => String(Math.round(toNumber(stored) * 100) / 100);

/**
 * A rule whose tax basis changes (an item's or a group's tax), its fixed price kept as it's shown: what was typed stays what it
 * says, now read the new way ($100 inc GST becomes $100 ex GST), so the amount before tax changes. The standard for every typed
 * amount when its tax is switched (QuotedAmount.tsx does the same for costs and supplier prices). A % has no basis.
 */
export function keepShownAmounts(rule: PricingRule, from: Basis, to: Basis): PricingRule {
    const keep = (stored: string) => storedAmount(shownAmount(stored, from), to);
    return { ...rule, basePrice: keep(rule.basePrice) };
}

/** The fixed price (stored before tax) that gives `price`: for keeping a price someone typed in ServiceM8. */
export const baseForPrice = (item: { price_includes_taxes: boolean }, tax: number, price: string) => toStored(toNet(toNumber(price), item.price_includes_taxes, tax));

/** What an item makes per unit, before tax (tax is never profit). */
export interface Profit {
    /** Its price less its cost. Null without a cost (nothing to tell it from). */
    profitNet: number | null;
    /** That as a markup on the cost (%), or null. */
    markupPct: number | null;
    /** Its cost, before tax. */
    costNet: number;
}

/** What an item's profit needs: its cost to update if it has one, and the tax rates. */
export type ProfitData = Pick<PriceListData, "costUpdates" | "taxRates">;

/** An item's cost as it's estimated: the cost it'll have once its cost to update is done (supplierPrices.ts costUpdates), else ServiceM8's. */
export const estimatedCost = (data: Pick<PriceListData, "costUpdates">, item: { uuid: string; cost: string }) => data.costUpdates[item.uuid] ?? item.cost;

/**
 * What an item makes per unit, estimated: from its price, and its cost as it will be once any cost to update is done
 * (estimatedCost). `was`: `item` is as it will be (e.g. taxed another way by a price update, its price the one it'll have), and
 * `was` as ServiceM8 has it now: its cost is worked out from that, before tax, so it's the same estimate whichever way it's
 * taxed. Every "what it makes" uses this, so they all agree. See Profit.
 */
export function itemProfit(data: ProfitData, item: CostedItem & { price: string }, tax: number, opts: { was?: CostedItem } = {}): Profit {
    const priceNet = toNet(toNumber(item.price), item.price_includes_taxes, tax);
    const now = opts.was ?? item;
    const nowTax = opts.was ? fraction(rateFor(now.tax_rate_uuid, data.taxRates)) : tax;
    const costNet = toNet(toNumber(estimatedCost(data, now)), now.price_includes_taxes, nowTax);
    if (costNet <= 0) return { profitNet: null, markupPct: null, costNet };
    // To the cent, so float leftovers don't show as a loss of $0.00.
    const profitNet = roundCents(priceNet - costNet) || 0;
    return { profitNet, markupPct: (profitNet / costNet) * 100, costNet };
}

/** A markup under the minimum (Price List settings) is flagged; with no minimum, only making nothing or a loss. Null (no cost): never. */
export const underMinimum = (markupPct: number | null, minMarkup: number) => markupPct !== null && markupPct < Math.max(minMarkup, 0.0001);

/** ServiceM8 may round differently; closer than this counts as the same price. */
const SAME_PRICE = 0.006;
export const samePrice = (a: string | number, b: string | number) => Math.abs(toNumber(a) - toNumber(b)) < SAME_PRICE;

export interface NeedsCheck {
    /** The price its pricing gives now (in `taxTo`'s basis, when that's set). */
    target: string;
    /** "edited": changed in ServiceM8 (not what this app last saved); "changed": its cost or tax moved. */
    reason: "edited" | "changed";
    /** It uses a group that taxes it otherwise: updating it also sets this. */
    taxTo: GroupTaxBasis | null;
}

/**
 * Whether an item's price needs checking: its pricing now gives a different price than it has, or
 * (using a fixed-price group) the group taxes it otherwise. Prices never change by themselves; someone updates or
 * keeps each one. One ServiceM8 prices itself never does.
 */
export function needsCheck(
    item: { cost: string; price: string; price_includes_taxes: boolean },
    rate: ItemRate,
    pricing: ItemPricing | undefined,
): NeedsCheck | null {
    // ServiceM8 prices it itself (its pricing strategy): nothing here to update it to.
    if (pricing?.pricedInServiceM8) return null;
    const priced = pricedAs(item, rate, pricing);
    const result = calcPrice(priced.item, priced.tax, pricing);
    if (!result || !pricing || (!priced.taxTo && samePrice(result.price, item.price))) return null;
    const edited = pricing.appliedPrice !== null && !samePrice(pricing.appliedPrice, item.price);
    return { target: result.price, reason: edited ? "edited" : "changed", taxTo: priced.taxTo };
}

/** ServiceM8's Smart Labour rule: an item whose name says Labour (or Labor) is labour, in its reports and on jobs. */
export const isLabour = (item: { name: string }) => LABOUR_WORD.test(item.name);

/** A labour time's share of its rate's amount (an hour's, before tax): 20 min of $180/hr is $60. */
export const labourShare = (hourlyNet: number, minutes: number) => (hourlyNet * minutes) / 60;
